import type {
  AccountKey,
  ArticleConfig,
  LimitWindow,
  MaterialFormat,
} from "./types";

/** Instagram portrait post dimensions (4:5) used by the image renderer. */
export const IMAGE_WIDTH = 1080 as const;
export const IMAGE_HEIGHT = 1350 as const;

/**
 * A render layout family for marketing materials. The same template adapts to a
 * format's exact dimensions because the templates are sized in viewport units
 * (so the PNG screenshot viewport and the PDF paper size both lay out correctly).
 */
export type MaterialTemplate = "portrait" | "landscape" | "tall" | "trifold";

/** Definition of one marketing-material format. */
export interface MaterialFormatDef {
  /** Preset key, or "custom" for an operator-sized material. */
  key: string;
  label: string;
  /** Render layout family (template file). */
  template: MaterialTemplate;
  /** Pixel viewport for the high-res PNG screenshot (a 2× device scale is applied). */
  pxWidth: number;
  pxHeight: number;
  /** Physical paper size (mm) for the print-ready PDF. */
  mmWidth: number;
  mmHeight: number;
  /** Short human description of the typical physical size / use. */
  hint: string;
}

/**
 * Marketing-material formats (offline/print collateral — NOT social/web posts).
 * Templates are sized in viewport units, so px (PNG) and mm (PDF) share one
 * layout. Portrait formats share the A-series ratio; spanduk is a wide banner;
 * x-banner is a tall roll-up.
 */
export const MATERIAL_FORMATS: Record<MaterialFormat, MaterialFormatDef> = {
  flyer: {
    key: "flyer",
    label: "Flyer (A5)",
    template: "portrait",
    pxWidth: 1240,
    pxHeight: 1754,
    mmWidth: 148,
    mmHeight: 210,
    hint: "A5 potret · selebaran",
  },
  poster: {
    key: "poster",
    label: "Poster (A4)",
    template: "portrait",
    pxWidth: 1240,
    pxHeight: 1754,
    mmWidth: 210,
    mmHeight: 297,
    hint: "A4 potret · poster/pengumuman",
  },
  brosur: {
    key: "brosur",
    label: "Brosur (A4)",
    template: "portrait",
    pxWidth: 1240,
    pxHeight: 1754,
    mmWidth: 210,
    mmHeight: 297,
    hint: "A4 potret · brosur 1 halaman",
  },
  trifold: {
    key: "trifold",
    label: "Brosur Trifold",
    template: "trifold",
    pxWidth: 2400,
    pxHeight: 1697,
    mmWidth: 297,
    mmHeight: 210,
    hint: "A4 lanskap · lipat 3 (3 panel)",
  },
  spanduk: {
    key: "spanduk",
    label: "Spanduk / Banner",
    template: "landscape",
    pxWidth: 2400,
    pxHeight: 800,
    mmWidth: 1200,
    mmHeight: 400,
    hint: "120×40 cm · spanduk lebar (3:1)",
  },
  xbanner: {
    key: "xbanner",
    label: "X-Banner (roll-up)",
    template: "tall",
    pxWidth: 900,
    pxHeight: 2400,
    mmWidth: 600,
    mmHeight: 1600,
    hint: "60×160 cm · roll-up banner",
  },
  square: {
    key: "square",
    label: "Persegi",
    template: "portrait",
    pxWidth: 1500,
    pxHeight: 1500,
    mmWidth: 250,
    mmHeight: 250,
    hint: "25×25 cm · serbaguna (1:1)",
  },
};

/** Bounds for a custom material's physical size, in millimetres. */
export const CUSTOM_SIZE_MIN_MM = 50; // 5 cm
export const CUSTOM_SIZE_MAX_MM = 5000; // 500 cm

/**
 * Builds a {@link MaterialFormatDef} for a custom (operator-sized) material from
 * physical mm dimensions. The layout family is chosen by aspect ratio and the
 * pixel viewport is scaled so the long edge is ~2000px (then a 2× device scale
 * is applied at render), giving a crisp PNG without an unbounded canvas.
 */
export function buildCustomFormatDef(
  widthMm: number,
  heightMm: number,
): MaterialFormatDef {
  const w = Math.max(CUSTOM_SIZE_MIN_MM, Math.min(CUSTOM_SIZE_MAX_MM, Math.round(widthMm)));
  const h = Math.max(CUSTOM_SIZE_MIN_MM, Math.min(CUSTOM_SIZE_MAX_MM, Math.round(heightMm)));
  const ratio = w / h;

  const template: MaterialTemplate =
    ratio >= 1.6 ? "landscape" : ratio <= 0.55 ? "tall" : "portrait";

  // Scale so the long edge ≈ 2000px (clamped 1200..2400), preserving the ratio.
  const longMm = Math.max(w, h);
  const targetLongPx = Math.max(1200, Math.min(2400, 2000));
  const scale = targetLongPx / longMm;
  const pxWidth = Math.max(1, Math.round(w * scale));
  const pxHeight = Math.max(1, Math.round(h * scale));

  const cm = (mm: number): string => (mm / 10).toString().replace(/\.0$/, "");
  return {
    key: "custom",
    label: `Custom (${cm(w)}×${cm(h)} cm)`,
    template,
    pxWidth,
    pxHeight,
    mmWidth: w,
    mmHeight: h,
    hint: `${cm(w)}×${cm(h)} cm`,
  };
}

/**
 * Acme brand palette — derived from the official logo (sampled): the wordmark
 * is slate-blue (#506080) and the mark is a cyan→teal-green gradient. `navy` is
 * a deep slate-navy base in the same blue family; `coral` is retained as a
 * legacy warm accent but is no longer used in the templates.
 */
export const BRAND_COLORS = {
  navy: "#0E1A2E",
  teal: "#33BEBA",
  green: "#36BBA6",
  slate: "#506080",
  coral: "#D2674B",
} as const;

export type BrandColorName = keyof typeof BRAND_COLORS;

/**
 * Shape of the Reguler cadence config stored under Setting key
 * `regular_config:<accountKey>`. Mirrors RegularConfig in @app/core scheduling.
 */
export interface RegularConfig {
  /** Posts per active day (1|2|3). */
  postsPerDay: number;
  /** Active weekdays, 0=Sunday .. 6=Saturday. */
  activeDays: number[];
  /** Suggested PUBLISH time-of-day for generated posts, "HH:mm". */
  suggestedTime: string;
  /**
   * Time-of-day this brand's daily generation RUNS, "HH:mm". Per-brand so loads
   * are staggered across brands (the worker schedules each brand at this time).
   */
  generateTime: string;
  /** Ordered pillars rotated weekly. */
  pillarRotation: string[];
  /**
   * When true, this brand's DAILY reguler auto-generation produces a CAROUSEL
   * (topic derived from the day's pillar) instead of a single-image post. Default
   * false — reguler stays single-image. The standalone Carousel menu is unaffected.
   */
  carousel?: boolean;
}

/**
 * Default Reguler cadence (per pillars.md §4). MVP uses a denser Mon–Sat cadence
 * ([1..6]) for easier end-to-end testing of the single `acme` account, rather
 * than the sparser Mon/Wed/Fri calendar — documented per BUILD_CONTRACT §7.
 */
export const DEFAULT_REGULAR_CONFIG: RegularConfig = {
  postsPerDay: 1,
  activeDays: [1, 2, 3, 4, 5, 6],
  suggestedTime: "09:00",
  generateTime: "06:00",
  pillarRotation: ["K1", "K2", "K3", "K4"],
  carousel: false,
};

/**
 * Default Article cadence/SEO config (per SPEC §12). All operator-facing fields
 * start empty; workers fall back to this when `article_config:<key>` is missing.
 * Publishes twice a week (Mon & Thu) at ~600 words, proposing 3 topics per run.
 */
export const DEFAULT_ARTICLE_CONFIG: ArticleConfig = {
  targetKeywords: [],
  articleLengthWords: 600,
  frequencyPerWeek: 2,
  publishDays: [1, 4],
  topicCount: 3,
  ctaText: "",
  ctaUrl: "",
  wpCategoryId: null,
  wpAuthorId: null,
};

/**
 * Helpers for building Setting keys so callers never hand-concatenate the
 * `<namespace>:<id>` format.
 */
export const SETTING_KEYS = {
  /** Per-account Reguler cadence config: `regular_config:<accountKey>`. */
  regularConfig: (accountKey: AccountKey): string => `regular_config:${accountKey}`,
  /** Per-account Article cadence/SEO config: `article_config:<accountKey>`. */
  articleConfig: (accountKey: AccountKey): string => `article_config:${accountKey}`,
  /**
   * Global default hero-image source for generated posts ("pexels" | "library").
   * Read by every generator that has no explicit per-request override; defaults
   * to "pexels" when the setting is absent.
   */
  imageSource: "image_source",
} as const;

/**
 * Content modules (the workspace top-nav menus) that an admin can enable/disable
 * per brand. `key` matches the nav route's module; `scheduled` modules also have
 * a daily auto-generation scheduler that respects this enablement.
 */
export const CONTENT_MODULES = [
  { key: "reguler", label: "Reguler", scheduled: true },
  { key: "special", label: "Special Case", scheduled: true },
  { key: "article", label: "Artikel", scheduled: true },
  { key: "custom", label: "Custom", scheduled: false },
  { key: "carousel", label: "Carousel", scheduled: false },
  { key: "media", label: "Galeri", scheduled: false },
  { key: "material", label: "Materi", scheduled: false },
  { key: "video", label: "Video", scheduled: false },
] as const;

export type ContentModule = (typeof CONTENT_MODULES)[number]["key"];

/** All module keys. An empty brand `modules` array is treated as "all of these". */
export const ALL_CONTENT_MODULES: ContentModule[] = CONTENT_MODULES.map(
  (m) => m.key,
);

/** Resolve a brand's effective enabled modules (empty/undefined ⇒ all). */
export function effectiveModules(modules: string[] | undefined): string[] {
  return modules && modules.length > 0 ? modules : ALL_CONTENT_MODULES;
}

/**
 * Actions an admin can put a per-brand usage quota on: each generate menu plus
 * draft revision. `key` matches the BrandLimits field + the enforcement point.
 */
export const LIMIT_MENUS = [
  { key: "reguler", label: "Generate Reguler" },
  { key: "special", label: "Generate Special" },
  { key: "article", label: "Generate Artikel" },
  { key: "custom", label: "Generate Custom" },
  { key: "material", label: "Generate Materi" },
  { key: "video", label: "Generate Video" },
  { key: "revise", label: "Revisi draft" },
] as const;

export type LimitMenuKey = (typeof LIMIT_MENUS)[number]["key"];

/** Selectable quota windows (UI labels). */
export const LIMIT_WINDOWS = [
  { key: "day", label: "per hari" },
  { key: "week", label: "per minggu" },
  { key: "month", label: "per bulan" },
] as const;

/**
 * Start of the current quota window (local calendar): day → 00:00 today;
 * week → Monday 00:00; month → the 1st at 00:00.
 */
export function limitWindowStart(window: LimitWindow, now: Date): Date {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (window === "week") {
    const sinceMonday = (day.getDay() + 6) % 7;
    return new Date(day.getFullYear(), day.getMonth(), day.getDate() - sinceMonday);
  }
  if (window === "month") return new Date(now.getFullYear(), now.getMonth(), 1);
  return day;
}
